// ============================================================
// 디자인 토큰 — 모든 페이지에서 이 상수를 참조해 className 통일
// 색상: --mint #57c4c6 / --mint-dark #2aa7aa / --sun #ffc34d / --ink #0f172a
// ============================================================

/** 페이지 공통 배경 (min-h-screen 포함) */
export const PAGE_BG = "min-h-screen bg-gradient-to-br from-teal-50 via-white to-white";

/** 메인 컨테이너 (max-w-6xl 기준) */
export const CONTAINER = "mx-auto w-full max-w-6xl px-4 py-10";

/** 섹션 카드 기본 */
export const CARD = "bg-white rounded-2xl shadow-sm ring-1 ring-slate-200";

/** 카드 + 기본 padding */
export const CARD_PADDED = `${CARD} p-6`;

// ──────────────────────────────────────────────
// 버튼
// ──────────────────────────────────────────────

/** 기본(primary) 버튼 — 민트 그라데이션 */
export const BTN_PRIMARY =
  "inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed";

/** 보조(secondary) 버튼 — 흰 배경 + 테두리 */
export const BTN_SECONDARY =
  "inline-flex items-center justify-center rounded-2xl ring-1 ring-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

/** 위험(danger) 버튼 — 로즈 */
export const BTN_DANGER =
  "inline-flex items-center justify-center rounded-2xl bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

/** 소형 primary */
export const BTN_PRIMARY_SM =
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed";

/** 소형 secondary */
export const BTN_SECONDARY_SM =
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl ring-1 ring-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

/** 소형 danger */
export const BTN_DANGER_SM =
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-rose-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

// ──────────────────────────────────────────────
// 폼 요소
// ──────────────────────────────────────────────

/** 텍스트 / 전화 / 이메일 입력 */
export const INPUT =
  "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800 outline-none ring-[#57c4c6]/40 focus:border-[#57c4c6] focus:ring-4 disabled:bg-slate-50 disabled:cursor-not-allowed transition-shadow";

/** 셀렉트 박스 */
export const SELECT =
  "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800 outline-none ring-[#57c4c6]/40 focus:border-[#57c4c6] focus:ring-4 disabled:bg-slate-50 bg-white transition-shadow";

/** 텍스트에리어 */
export const TEXTAREA =
  "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800 outline-none ring-[#57c4c6]/40 focus:border-[#57c4c6] focus:ring-4 disabled:bg-slate-50 resize-none transition-shadow";

/** 라벨 */
export const LABEL = "block text-xs font-semibold text-slate-500 mb-1.5";

// ──────────────────────────────────────────────
// 알림 박스
// ──────────────────────────────────────────────

/** 에러 박스 */
export const ALERT_ERROR =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700";

/** 성공 박스 */
export const ALERT_SUCCESS =
  "rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700";

/** 정보 박스 (민트/틸) */
export const ALERT_INFO =
  "rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700";

/** 경고 박스 (앰버) */
export const ALERT_WARNING =
  "rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700";

// ──────────────────────────────────────────────
// 타이포
// ──────────────────────────────────────────────

/** 페이지 메인 제목 */
export const HEADING_PAGE = "text-2xl font-extrabold tracking-tight text-[#0f172a]";

/** 섹션 제목 */
export const HEADING_SECTION = "text-lg font-bold tracking-tight text-[#0f172a]";

/** 본문 보조 텍스트 */
export const TEXT_MUTED = "text-sm text-slate-500";

// ──────────────────────────────────────────────
// CPI 티어 시각화
// ──────────────────────────────────────────────

export type TierName =
  | "IRON" | "BRONZE" | "SILVER" | "GOLD"
  | "PLATINUM" | "DIAMOND" | "MASTER" | "CHALLENGER";

export const TIER_CONFIG: Record<TierName, {
  label: string;
  dotColor: string;   // Tailwind bg 클래스
  textColor: string;  // Tailwind text 클래스
  borderColor: string;// Tailwind border 클래스
  bgLight: string;    // 카드 배경 (연한 색)
  badgeImage: string; // /badges/ 경로 PNG
}> = {
  IRON:       { label: "IRON",       dotColor: "bg-gray-400",    textColor: "text-gray-500",    borderColor: "border-gray-300",   bgLight: "bg-gray-50",    badgeImage: "/badges/iron.png"       },
  BRONZE:     { label: "BRONZE",     dotColor: "bg-orange-400",  textColor: "text-orange-500",  borderColor: "border-orange-300", bgLight: "bg-orange-50",  badgeImage: "/badges/bronze.png"     },
  SILVER:     { label: "SILVER",     dotColor: "bg-slate-400",   textColor: "text-slate-500",   borderColor: "border-slate-300",  bgLight: "bg-slate-50",   badgeImage: "/badges/silver.png"     },
  GOLD:       { label: "GOLD",       dotColor: "bg-yellow-400",  textColor: "text-yellow-600",  borderColor: "border-yellow-300", bgLight: "bg-yellow-50",  badgeImage: "/badges/gold.png"       },
  PLATINUM:   { label: "PLATINUM",   dotColor: "bg-teal-400",    textColor: "text-teal-600",    borderColor: "border-teal-300",   bgLight: "bg-teal-50",    badgeImage: "/badges/platinum.png"   },
  DIAMOND:    { label: "DIAMOND",    dotColor: "bg-blue-400",    textColor: "text-blue-600",    borderColor: "border-blue-300",   bgLight: "bg-blue-50",    badgeImage: "/badges/diamond.png"    },
  MASTER:     { label: "MASTER",     dotColor: "bg-purple-500",  textColor: "text-purple-600",  borderColor: "border-purple-300", bgLight: "bg-purple-50",  badgeImage: "/badges/master.png"     },
  CHALLENGER: { label: "CHALLENGER", dotColor: "bg-cyan-400",    textColor: "text-cyan-600",    borderColor: "border-cyan-300",   bgLight: "bg-cyan-50",    badgeImage: "/badges/challenger.png" },
};

export const TIER_LEVEL_LABEL: Record<number, string> = { 1: "I", 2: "II", 3: "III" };
